import React, { useCallback } from 'react'
import { connect } from 'react-redux'
import { setModal } from '@hixme/modal'
import { isNil } from 'lodash'

import { createErrorNotification } from 'modules/notification-manager/actions'
import { selectors as userSessionSelectors, actions as userSessionActions } from 'store/modules/user-session'
import * as personSelectors from 'store/modules/persons/selectors'
import { HELP } from 'modal-constants'

import Header from 'components/Header'

const SWITCH_EMPLOYER_ERROR_MESSAGE =
  'Unable to change company right now. Please try again later.'

const getSwitchEmployerErrorMessage = (err) => {
  const rawMessage = err?.response?.status === 409 ? err?.response?.data?.message : null
  const epsMessage = Array.isArray(rawMessage) ? rawMessage[0] : rawMessage
  if (typeof epsMessage === 'string' && epsMessage.trim().length > 0) {
    return epsMessage
  }
  return SWITCH_EMPLOYER_ERROR_MESSAGE
}

const HeaderContainer = ({
  companyName,
  hideCartSummary,
  isGuestUser,
  currentPersonId,
  memberships,
  sessionIsUpdating,
  isImpersonating,
  showHelpModal,
  showSwitchEmployerError,
  switchActingEmployer,
}) => {
  const onEmployerChange = useCallback(
    (personId) => {
      // Admin/broker Login-As: never exchange into another company identity.
      if (sessionIsUpdating || isImpersonating) return
      if (!isNil(personId) && personId !== currentPersonId) {
        // Catch so exchange failures do not become unhandled rejections in RUM.
        // Bootstrap-restore failures are already notified inside switchActingEmployer.
        void switchActingEmployer(personId).catch((err) => {
          if (err?.message === 'Employer switch bootstrap failed') {
            return
          }
          showSwitchEmployerError(getSwitchEmployerErrorMessage(err))
        })
      }
    },
    [
      currentPersonId,
      isImpersonating,
      sessionIsUpdating,
      showSwitchEmployerError,
      switchActingEmployer,
    ]
  )

  return (
    <Header
      showHelpModal={showHelpModal}
      companyName={companyName}
      hideCartSummary={hideCartSummary}
      isGuestUser={isGuestUser}
      // Spoof session: static company pill only — no multi-employer switcher.
      memberships={isImpersonating ? [] : memberships}
      currentPersonId={currentPersonId}
      onEmployerChange={onEmployerChange}
      employerSwitchDisabled={sessionIsUpdating || isImpersonating}
    />
  )
}

const mapStateToProps = (state) => {
  const person = personSelectors.getPersonSelector(state)
  const companyName = userSessionSelectors.getCompanyName(state)
  const enrollmentIsActive = userSessionSelectors.getEnrollmentIsActiveSelector(state)
  const isCartCommitted = userSessionSelectors.isCartCommitted(state)
  const isEnrollingExcludeQLE = userSessionSelectors.isEnrollingExcludeQLE(state)
  const isGuestUser = userSessionSelectors.isGuestUser(state)
  const currentPersonId = userSessionSelectors.getUserId(state)
  const memberships = userSessionSelectors.getMemberships(state)
  const sessionIsUpdating = userSessionSelectors.sessionIsUpdating(state)
  const isImpersonating = userSessionSelectors.isImpersonating(state)

  const hideCartSummary =
    !person.VerifiedPersonalInfo || !isEnrollingExcludeQLE || enrollmentIsActive || isCartCommitted

  return {
    companyName,
    hideCartSummary,
    isGuestUser,
    currentPersonId,
    memberships,
    sessionIsUpdating,
    isImpersonating,
  }
}

export default connect(mapStateToProps, (dispatch) => ({
  showHelpModal: () => {
    dispatch(setModal(HELP))
  },
  showSwitchEmployerError: (message) => {
    dispatch(createErrorNotification(message))
  },
  switchActingEmployer: (personId) => dispatch(userSessionActions.switchActingEmployer(personId)),
}))(HeaderContainer)
